import React, { useState } from 'react';
import formStyles from './StrategyForm.module.css';
import styles from './StepReview.module.css';
import { MODE_OPTIONS } from './ModeMultiSelect';

// Mode is a multi-select array — render it as a comma-separated list of the
// human labels (e.g. "Signal, Trade"). Tolerates a legacy string just in case.
const formatModes = (value) => {
  const arr = Array.isArray(value) ? value : value ? [value] : [];
  if (!arr.length) return '—';
  return arr
    .map((v) => (MODE_OPTIONS.find((o) => o.value === v) || {}).label || v)
    .join(', ');
};

/**
 * Step 3 — Review & Confirm: read-only summary of the strategy
 * before the user hits Save.
 *
 * Purely presentational. formData and renderConditionSummary come
 * from the parent StrategyForm orchestrator.
 */
const StepReview = ({ formData, renderConditionSummary }) => {
  const [symbolsOpen, setSymbolsOpen] = useState(false);

  return (
  <div className={formStyles.stepContent}>
    <h2 className={formStyles.stepTitle}>Review &amp; Confirm</h2>
    <p className={formStyles.stepDescription}>Review your strategy before saving.</p>

    <div className={styles.reviewCard}>
      <div className={styles.reviewHeader}>
        <h3>{formData.name || 'Untitled Strategy'}</h3>
        <span className={`${styles.actionBadge} ${formData.action === 'BUY' ? styles.buyBadge : styles.sellBadge}`}>
          {formData.action}
        </span>
      </div>

      <div className={styles.reviewGrid}>
        <div className={styles.reviewItem}>
          <span className={styles.reviewLabel}>Mode</span>
          <span className={styles.reviewValue}>{formatModes(formData.alert_or_trade)}</span>
        </div>
        <div className={styles.reviewItem}>
          <span className={styles.reviewLabel}>Conditions</span>
          <span className={styles.reviewValue}>{formData.conditions.length}</span>
        </div>
        <div className={styles.reviewItem}>
          <span className={styles.reviewLabel}>Position Sizing</span>
          <span className={styles.reviewValue}>
            {formData.position_sizing
              ? (formData.position_sizing.type === 'fixed_quantity'
                  ? `${Number(formData.position_sizing.value).toLocaleString('en-IN')} shares / trade`
                  : `₹${Number(formData.position_sizing.value).toLocaleString('en-IN')} / trade`)
              : 'Not configured'}
          </span>
        </div>
        <div className={styles.reviewItem}>
          <span className={styles.reviewLabel}>Stop Loss</span>
          <span className={styles.reviewValue}>
            {formData.trailing_stop_loss
              ? (formData.trailing_stop_loss.type === 'fixed'
                  ? `Fixed ${Number(formData.trailing_stop_loss.value)}% from entry`
                  : formData.trailing_stop_loss.type === 'percent'
                    ? `${Number(formData.trailing_stop_loss.value)}% trail`
                    : `Stepped (Initial: ${formData.trailing_stop_loss.initial_stop_pct}%, Hurdle: ${formData.trailing_stop_loss.hurdle_pct}%, Step: ${formData.trailing_stop_loss.step_pct}%)`)
              : 'Not configured'}
          </span>
        </div>
        <div className={styles.reviewItem}>
          <span className={styles.reviewLabel}>Target</span>
          <span className={styles.reviewValue}>
            {formData.target
              ? (formData.target.type === 'percent'
                  ? `${Number(formData.target.value)}% from entry`
                  : `₹${Number(formData.target.value).toLocaleString('en-IN')} (fixed price)`)
              : 'Not configured'}
          </span>
        </div>
        <div className={styles.reviewItem}>
          <span className={styles.reviewLabel}>Brokerage &amp; Taxes</span>
          <span className={styles.reviewValue}>
            {formData.brokerage_config
              ? `Buy ${Number(formData.brokerage_config.buy_pct)}% · Sell ${Number(formData.brokerage_config.sell_pct)}%`
              : 'Not configured'}
          </span>
        </div>
      </div>

      {formData.conditions.length > 0 && (
        <div className={styles.reviewConditions}>
          <h4>When:</h4>
          {formData.conditions.map((cond, i) => (
            <div key={i} className={styles.reviewCondLine}>
              <span className={styles.condPill}>{renderConditionSummary(cond)}</span>
              {i < formData.conditions.length - 1 && (
                <span className={styles.condLogic}>{cond.logicalOperator || 'AND'}</span>
              )}
            </div>
          ))}
          <p className={styles.reviewSummary}>
            → <strong>{formData.action}</strong> + {formatModes(formData.alert_or_trade)}
          </p>

          {/* Collapsible symbols dropdown */}
          <div className={`${styles.reviewSymbolsDropdown} ${symbolsOpen ? styles.reviewSymbolsOpen : ''}`}>
            <button
              type="button"
              className={styles.reviewSymbolsToggle}
              onClick={() => setSymbolsOpen(prev => !prev)}
              aria-expanded={symbolsOpen}
            >
              <span className={styles.reviewSymbolsLabel}>Symbols</span>
              <span className={styles.reviewSymbolsBadge}>{formData.symbols.length}</span>
              <span className={`material-symbols-outlined ${styles.reviewSymbolsChevron}`}>
                expand_more
              </span>
            </button>
            {symbolsOpen && (
              <div className={styles.reviewSymbolsBody}>
                {formData.symbols.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

export default StepReview;
